import React, { useState, useEffect, useRef } from 'react';
import api from "../../utils/axiosInstance";
import HeaderBlue from '../../components/HeaderBlue';
import FooterBlue from '../../components/FooterBlue';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const DemoBooking = () => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [slots, setSlots] = useState([]);
    const [bookedSlots, setBookedSlots] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [form, setForm] = useState({ name: '', email: '', phone: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // NEW: State for success modal with booking details
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [bookingDetails, setBookingDetails] = useState(null);

    const videoRef = useRef(null);
    const [selected, setSelected] = useState(0);

    const demoSlots = [
        { slotNumber: 1, startTime: '08:00', endTime: '09:00' },
        { slotNumber: 2, startTime: '09:00', endTime: '10:00' },
        { slotNumber: 3, startTime: '10:00', endTime: '11:00' },
        { slotNumber: 4, startTime: '11:00', endTime: '12:00' },
        { slotNumber: 5, startTime: '13:00', endTime: '14:00' },
        { slotNumber: 6, startTime: '14:00', endTime: '15:00' },
        { slotNumber: 7, startTime: '15:00', endTime: '16:00' },
        { slotNumber: 8, startTime: '16:00', endTime: '17:00' },
    ];

    const demoVideos = [
        { title: 'How to LOGIN as a Carrier', url: '/demovideos/carrier-login.webm', preview: '/demovideos/carierlogin.webp' },
        { title: 'How to SIGN UP as a Carrier', url: '/demovideos/carriersSignup.webm', preview: '/demovideos/carierregsmall.webp' },
        { title: 'How to use Carrier Dashboard', url: '/demovideos/howToUseCarierDashboard.webm', preview: '/demovideos/dashboard.webp' },
        { title: 'How to Purchase a Package', url: '/demovideos/contactingSupport.webm', preview: '/demovideos/contact.webp' },
    ];


    useEffect(() => {
        if (!selectedDate) return;

        const fetchBookedSlots = async () => {
            try {
                const res = await api.get(
                    '/demo-slots/booked');
                setBookedSlots(res.data.bookedSlots || []);
                setSlots(demoSlots);
            } catch (err) {
                console.error('Error fetching booked slots:', err);
            }
        };

        fetchBookedSlots();
    }, [selectedDate]);

    const isSlotBooked = (slot) => {
        const formattedSelectedDate = new Date(selectedDate).toISOString().split('T')[0];

        const isBooked = bookedSlots.some((b) => {
            if (!b.date) return false; // Skip if date is missing
            const booked = new Date(b.date);
            if (isNaN(booked)) return false; // Skip invalid dates

            const bookedDate = booked.toISOString().split('T')[0];
            return bookedDate === formattedSelectedDate && b.startTime === slot.startTime;
        });

        const today = new Date();
        const selected = new Date(selectedDate);
        const isToday = today.toDateString() === selected.toDateString();

        if (isToday) {
            const [slotHour, slotMinute] = slot.startTime.split(':').map(Number);
            const slotTime = new Date(selected);
            slotTime.setHours(slotHour, slotMinute, 0, 0);
            if (slotTime <= today) return true;
        }

        return isBooked;
    };


    const handleBookClick = (slot) => {
        setSelectedSlot(slot);
        setModalOpen(true);
        setError('');
    };

    const handleFormChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const handleConfirmBooking = async () => {
        if (!form.name || !form.email || !form.phone) {
            setError('All fields are required');
            return;
        }

        try {
            setLoading(true);
            setError('');

            const res = await api.post(
                '/demo-slots/book-common',
                {
                    name: form.name,
                    email: form.email,
                    phone: form.phone,
                    date: selectedDate,
                    startTime: selectedSlot.startTime,
                    endTime: selectedSlot.endTime,
                }
            );

            // UPDATED: Store booking details including calendar info
            setBookingDetails({
                ...res.data.booking,
                calendarEvent: res.data.calendarEvent,
                message: res.data.message
            });

            setBookedSlots([
                ...bookedSlots,
                { slotId: `${selectedDate}-${selectedSlot.startTime}` },
            ]);

            // UPDATED: Close booking modal and open success modal
            setModalOpen(false);
            setSuccessModalOpen(true);

            setForm({ name: '', email: '', phone: '' });
            setSelectedSlot(null);
        } catch (err) {
            if (err.response && err.response.data) setError(err.response.data.message);
            else setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="bg-gray-100 min-h-screen flex flex-col font-oswald">
            <HeaderBlue />

            {/* Hero Section */}
            <section
                className="w-full py-60 relative bg-cover bg-center px-3"
                style={{
                    backgroundImage: "url('./assets/policyheader.webp')",
                    backgroundSize: 'cover',
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-white/10 z-0"></div>
                <div className="md:px-40 relative z-10">
                    <h2 className="text-white text-6xl font-oswald drop-shadow-lg pb-5">
                        See How Our Trucking Platform Works in Minutes
                    </h2>
                    <p className="text-white text-xl font-semibold font-oswald mt-2">
                        Choose the best way to explore our system – book a live demo with our team or watch the demo video instantly.
                    </p>
                </div>
            </section>

            {/* Split Section */}
            <main className="max-w-6xl mx-auto px-5 py-16 flex flex-col md:flex-row gap-8">
                {/* Left: Booking */}
                <div className="md:w-1/2 flex flex-col gap-6 pr-4">
                    {/* Heading and points above booking */}
                    <h1 className="text-3xl font-bold text-primary mb-4">BOOK A LIVE DEMO</h1>
                    <ul className="list-disc list-inside text-black mb-4 space-y-1">
                        <li>Schedule a convenient time with our product expert</li>
                        <li>Get a personalized walkthrough of features</li>
                        <li>Ask questions and see how the platform fits your business</li>
                    </ul>

                    {/* Booking Form */}
                    <h2 className="text-2xl font-bold text-primary mb-4">Book a Demo Slot</h2>

                    <p className="text-black mb-4">Select a date to view slots</p>
                    <DatePicker
                        selected={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        minDate={new Date()}
                        dateFormat="MM/dd/yyyy"
                        placeholderText="MM/DD/YYYY"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
                    />

                    <div className="grid grid-cols-2 gap-4">
                        {slots.map((slot) => {
                            const booked = isSlotBooked(slot);
                            return (
                                <div
                                    key={slot.slotNumber}
                                    className={`
          flex flex-col justify-center items-center
          p-4 border border-blue-500 rounded-xl transition
          font-semibold text-center cursor-pointer
          ${booked
                                            ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-60"
                                            : "bg-white hover:bg-blue-50 text-blue-700"}
        `}
                                    onClick={() => {
                                        if (!booked) handleBookClick(slot);
                                    }}
                                    aria-disabled={booked}
                                    style={{ minHeight: "80px" }}
                                >
                                    <span className="text-base">
                                        {slot.startTime} - {slot.endTime}
                                    </span>
                                    {booked && (
                                        <span className="block text-xs text-gray-500 font-medium mt-2">
                                            Booked
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="hidden md:block w-px bg-black h-auto self-stretch"></div>

                {/* Right: Demo Videos */}
                <div className="md:w-1/2 flex flex-col gap-6 pl-4">
                    <h1 className="text-3xl font-bold text-primary mb-4">WATCH DEMO VIDEO</h1>
                    <ul className="list-disc list-inside text-black mb-4 space-y-1">
                        <li>Quick, on-demand product tour</li>
                        <li>Learn how carriers and truck drivers use the system</li>
                        <li>No registration needed – start watching now</li>
                    </ul>

                    <ul className="space-y-3 w-full">
                        {demoVideos.slice(0, 6).map((video, idx) => (
                            <li key={video.title} className="w-full">
                                <button
                                    className={`w-full text-left px-3 py-2 rounded transition ${selected === idx ? 'bg-primary text-white font-semibold' : 'bg-gray-100 hover:bg-gray-200'
                                        }`}
                                    onClick={() => setSelected(selected === idx ? null : idx)}
                                >
                                    {video.title}
                                </button>

                                {/* Video Player toggled */}
                                {selected === idx && (
                                    <div className="mt-3 relative w-full aspect-video bg-black rounded-lg overflow-hidden shadow-lg">
                                        <video
                                            key={video.url}
                                            src={video.url}
                                            controls
                                            className="w-full h-full"
                                            poster={video.preview}
                                        >
                                            Sorry, your browser doesn't support embedded videos.
                                        </video>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            </main>

            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Confirm Booking</h2>
                        <div className="space-y-4">
                            <input
                                type="text"
                                name="name"
                                placeholder="Name"
                                value={form.name}
                                onChange={handleFormChange}
                                required
                                className="w-full border px-4 py-2 rounded"
                            />
                            <input
                                type="email"
                                name="email"
                                placeholder="Email"
                                value={form.email}
                                onChange={handleFormChange}
                                required
                                className="w-full border px-4 py-2 rounded"
                            />
                            <input
                                type="tel"
                                name="phone"
                                placeholder="Phone"
                                value={form.phone}
                                onChange={handleFormChange}
                                onInput={(e) => {
                                    let cleaned = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    if (cleaned.length > 6) {
                                        cleaned = `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
                                    } else if (cleaned.length > 3) {
                                        cleaned = `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
                                    }

                                    e.target.value = cleaned;
                                }}
                                pattern="\d{3}-\d{3}-\d{4}"
                                maxLength={12}
                                className="w-full border px-4 py-2 rounded"
                            />

                        </div>
                        {error && <div className="text-red-600 mt-2">{error}</div>}
                        <div className="flex justify-end space-x-2 mt-4">
                            <button
                                onClick={() => setModalOpen(false)}
                                className="px-4 py-2 bg-gray-300 rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmBooking}
                                disabled={loading}
                                className="px-4 py-2 bg-secondary text-white rounded disabled:opacity-50"
                            >
                                {loading ? 'Booking...' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {successModalOpen && bookingDetails && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
                        <div className="flex items-center justify-center mb-6">
                            <div className="bg-green-100 rounded-full p-3">
                                <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                        </div>

                        <h2 className="text-2xl font-bold text-center text-green-600 mb-2">
                            Booking Confirmed!
                        </h2>
                        <p className="text-center text-gray-600 mb-6">
                            Your demo slot has been successfully booked
                        </p>

                        <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-gray-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <span className="text-gray-700"><strong>Name:</strong> {bookingDetails.name}</span>
                            </div>

                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-gray-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="text-gray-700"><strong>Date:</strong> {formatDate(bookingDetails.date)}</span>
                            </div>

                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-gray-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-gray-700"><strong>Time:</strong> {bookingDetails.startTime} - {bookingDetails.endTime}</span>
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 mb-4">
                            <div className="flex items-start">
                                <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div className="text-sm text-blue-800">
                                    <p className="font-semibold mb-1">What's Next?</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Check your email for the calendar invite</li>
                                        <li>Add the event to your calendar</li>
                                        <li>You'll receive reminders before the meeting</li>
                                        <li>Use the Google Meet link to join at the scheduled time</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                setSuccessModalOpen(false);
                                setBookingDetails(null);
                            }}
                            className="w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition font-semibold"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            <FooterBlue />
        </div>
    );
};

export default DemoBooking;